import React, { useEffect, useState } from "react";
import { Modal, Button, Card, Row, Col, Form } from "react-bootstrap";
import { FaRegSquarePlus } from "react-icons/fa6";
import Loader from "../loader/Loader";
import config from "../../config/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { getAuthToken } from "../../utils/token";
import Select from "react-select";
import { formatDate } from "../../utils/formatDate";

export default function AddProductionPlanModal({
  show,
  handleClose,
  planId,
}) {
  const [productRows, setProductRows] = useState([
    {
      id: 1,
      item_id: null,
      product_name: "",
      months: {},
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [planList, setPlanList] = useState();
  const [productList, setProductList] = useState([]);

  const getNextElevenMonths = () => {
    const months = [
      { label: "January", value: 1 },
      { label: "February", value: 2 },
      { label: "March", value: 3 },
      { label: "April", value: 4 },
      { label: "May", value: 5 },
      { label: "June", value: 6 },
      { label: "July", value: 7 },
      { label: "August", value: 8 },
      { label: "September", value: 9 },
      { label: "October", value: 10 },
      { label: "November", value: 11 },
      { label: "December", value: 12 },
    ];

    const nextMonths = [];

    for (let i = 0; i < 12; i++) {
      nextMonths.push(months[i]);
    }

    return nextMonths;
  };

  const months = getNextElevenMonths();

  // Add new product row
  const addNewRow = () => {
    setProductRows((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        item_id: null,
        product_name: "",
        months: {},
      },
    ]);
  };

  // Handle input changes
  const handleInputChange = (
    rowId,
    field,
    value,
    monthValue = null
  ) => {
    setProductRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? monthValue !== null
            ? {
                ...row,
                months: {
                  ...row.months,
                  [monthValue]: value,
                },
              }
            : {
                ...row,
                [field]: value,
              }
          : row
      )
    );
  };

  // Fetch purchase order details
  const fetchPlaningActionList = async () => {
    if (!planId) return;

    const token = getAuthToken();

    if (!token) return;

    setLoading(true);

    try {
      const { data } = await axios.get(
        `${config.API}/purchaseorder/${planId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPlanList(data);
    } catch (err) {
      console.error(
        "Error fetching PO details:",
        err?.response?.data || err.message
      );

      toast.error("Failed to fetch purchase order details!");
    } finally {
      setLoading(false);
    }
  };

  // Fetch products
  const fetchProductList = async () => {
    const token = getAuthToken();

    if (!token) return;

    setLoading(true);

    try {
      const response = await axios.get(
        `${config.API}/product/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setProductList(response?.data?.products || []);
    } catch (err) {
      console.error("Error fetching products:", err.message);
      toast.error("Failed to fetch product list!");
    } finally {
      setLoading(false);
    }
  };

  // Product list
  useEffect(() => {
    fetchProductList();
  }, []);

  // Purchase order
  useEffect(() => {
    if (planId) {
      fetchPlaningActionList();
    }
  }, [planId]);

  // Product options
  const productOptions = productList.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  // Prefill product rows from PO items
  useEffect(() => {
    if (planList?.list?.length > 0) {
      const prefilled = planList.list.map((item, idx) => {
        const matchedProduct = productList.find(
          (p) => p.name === item.product_name
        );

        return {
          id: idx + 1,

          // IMPORTANT: preserve purchase order item ID
          item_id: item.item_id,

          product_name: matchedProduct
            ? matchedProduct.id
            : "",

          months: {},
        };
      });

      setProductRows(prefilled);
    } else {
      setProductRows([
        {
          id: 1,
          item_id: null,
          product_name: "",
          months: {},
        },
      ]);
    }
  }, [planId, planList, productList]);

  // Reset when modal closes
  useEffect(() => {
    if (!show) {
      setProductRows([
        {
          id: 1,
          item_id: null,
          product_name: "",
          months: {},
        },
      ]);
    }
  }, [show]);

  // Submit production plan
  const handleSubmit = async () => {
    if (!planList?.data?.id) {
      toast.error("Purchase order data missing!");
      return;
    }

    const finalPayload = [];

    productRows.forEach((row) => {
      if (!row.product_name) return;

      const product = productList.find(
        (item) => item.id === row.product_name
      );

      const production = [];

      Object.entries(row.months).forEach(
        ([monthValue, qty]) => {
          const qtyNum = Number(qty);

          if (qtyNum > 0) {
            production.push({
              month: Number(monthValue),
              qty: qtyNum,
            });
          }
        }
      );

      if (production.length) {
        finalPayload.push({
          // Purchase Order ID
          po_id: planList.data.id,

          // IMPORTANT: item ID from the current row
          item_id: row.item_id,

          // Product details
          product_id: product?.id,
          product_name: product?.name,

          // Monthly production
          production,
        });
      }
    });

    if (!finalPayload.length) {
      toast.warn(
        "Please add at least one product with a valid quantity."
      );
      return;
    }

    console.log("Production Plan Payload:", finalPayload);

    try {
      const token = getAuthToken();

      const res = await axios.post(
        `${config.API}/manager/ProductionPlan`,
        finalPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res?.data) {
        toast.success(
          res.data.message ||
            "Production plan saved successfully!"
        );

        setProductRows([
          {
            id: 1,
            item_id: null,
            product_name: "",
            months: {},
          },
        ]);

        handleClose();
      } else {
        toast.error(
          res?.data?.message ||
            "Failed to save production plan!"
        );
      }
    } catch (err) {
      console.error(
        "Error submitting production plan:",
        err
      );

      toast.error(
        err?.response?.data?.message ||
          "Something went wrong!"
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="font_family"
    >
      <Modal.Header closeButton>
        <strong className="fs-4">
          Addition to Production Plan (PO)
        </strong>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <Loader />
        ) : (
          <>
            {/* Purchase Order Details */}
            {planList?.data && (
              <Card
                className="mb-3 mt-3 shadow-sm border-0"
                style={{ background: "#F5F8F9" }}
              >
                <Card.Body>
                  <Row className="mb-2">
                    <Col md={3}>
                      <strong>PO Number:</strong>{" "}
                      {planList.data.po_number}
                    </Col>

                    <Col md={3}>
                      <strong>Consignee:</strong>{" "}
                      {planList.data.consignee}
                    </Col>

                    <Col md={3}>
                      <strong>Inspection Type:</strong>{" "}
                      {planList.data.inspection_type}
                    </Col>

                    <Col md={3}>
                      <strong>Entry Type:</strong>{" "}
                      {planList.data.entryType}
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <strong>PO Type:</strong>{" "}
                      {planList.data.poType}
                    </Col>

                    <Col md={3}>
                      <strong>Delivery Date:</strong>{" "}
                      {formatDate(
                        planList.data.delivery_date
                      )}
                    </Col>

                    <Col md={3}>
                      <strong>PO Date:</strong>{" "}
                      {formatDate(
                        planList.data.po_date
                      )}
                    </Col>

                    <Col md={3}>
                      <strong>
                        Tentative Inspection Date:
                      </strong>{" "}
                      {formatDate(
                        planList.data
                          .tentative_inspection_date
                      )}
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <strong>Company Code:</strong>{" "}
                      {planList.data.companyCode}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* PO Items */}
            {planList?.list?.length > 0 && (
              <Card
                className="mb-4 shadow-sm border-0"
                style={{ background: "#F5F8F9" }}
              >
                <Card.Body>
                  {planList.list.map((item, idx) => (
                    <Row
                      className="mb-2"
                      key={idx}
                    >
                      <Col md={3}>
                        <strong>Product Name:</strong>{" "}
                        {item.product_name}
                      </Col>

                      <Col md={2}>
                        <strong>Item ID:</strong>{" "}
                        {item.item_id}
                      </Col>

                      <Col md={2}>
                        <strong>Consignee:</strong>{" "}
                        {item.consignee}
                      </Col>

                      <Col md={2}>
                        <strong>Quantity:</strong>{" "}
                        {item.quantity}
                      </Col>

                      <Col md={2}>
                        <strong>Remaining:</strong>{" "}
                        {item.quantity -
                          item.dispatch_qty}
                      </Col>

                      <Col md={2}>
                        <strong>Dispatch Qty:</strong>{" "}
                        {item.dispatch_qty}
                      </Col>

                      <Col md={3}>
                        <strong>
                          Delivery Date:
                        </strong>{" "}
                        {formatDate(
                          item.delivery_date
                        )}
                      </Col>

                      <Col md={3}>
                        <strong>
                          Inspection Date:
                        </strong>{" "}
                        {formatDate(
                          item.inspection_date
                        )}
                      </Col>

                      <Col md={6}>
                        <strong>
                          Stock of{" "}
                          {item.product_name}:
                        </strong>{" "}
                        {item.unit_produced ?? 0}
                      </Col>
                    </Row>
                  ))}
                </Card.Body>
              </Card>
            )}
          </>
        )}

        {/* Production Plan */}
        <h6 className="fw-bold mb-3">
          Production Plan
        </h6>

        <Form>
          {productRows.map((row) => (
            <Row
              className="align-items-center mb-3"
              key={row.id}
            >
              <Col md={3}>
                <label>Product Name</label>

                <Select
                  value={
                    productOptions.find(
                      (opt) =>
                        opt.value ===
                        row.product_name
                    ) || null
                  }
                  onChange={(selected) =>
                    handleInputChange(
                      row.id,
                      "product_name",
                      selected?.value || ""
                    )
                  }
                  options={productOptions}
                  placeholder={
                    loading
                      ? "Loading..."
                      : "Select Product"
                  }
                  isClearable
                />

                {/* Hidden item_id */}
                <Form.Control
                  type="hidden"
                  value={row.item_id || ""}
                  readOnly
                />
              </Col>

              {months.map((month) => (
                <Col
                  md={3}
                  key={month.value}
                >
                  <label>
                    {month.label} Production
                  </label>

                  <Form.Control
                    type="number"
                    min="0"
                    placeholder={`${month.label} Qty`}
                    value={
                      row.months[
                        month.value
                      ] || ""
                    }
                    onChange={(e) =>
                      handleInputChange(
                        row.id,
                        "months",
                        e.target.value,
                        month.value
                      )
                    }
                  />
                </Col>
              ))}
            </Row>
          ))}

          {/* Add New Product */}
          <div
            className="d-flex align-items-center mb-4"
            onClick={addNewRow}
            style={{ cursor: "pointer" }}
          >
            <span className="me-2">
              <FaRegSquarePlus />
            </span>

            <span className="p-0 text-dark">
              Add New Product
            </span>
          </div>

          {/* Submit */}
          <div className="text-end">
            <Button
              variant="dark"
              onClick={handleSubmit}
            >
              Update Plan
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}